import { useState, useEffect, useRef } from "react";

const VALUES = [100, 200, 300, 400, 500];
const CAT_COLORS = ["#ff3cac", "#00f5ff", "#ffe600", "#3dffa0", "#9b5de5"];

export default function JeopardyPlayer({ board, result, playerName, onPick, onBuzz, onAnswer }) {
  const [buzzed,    setBuzzed]    = useState(false);
  const [pressing,  setPressing]  = useState(false);
  const [typed,     setTyped]     = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showRes,   setShowRes]   = useState(null);
  const inputRef = useRef(null);

  // Сброс при новом вопросе
  useEffect(() => {
    if (board?.phase === "question") {
      setBuzzed(false); setTyped(""); setSubmitted(false); setShowRes(null);
    }
  }, [board?.phase, board?.current?.question]);

  // Фокус на инпут когда я печатаю
  useEffect(() => {
    if (board?.phase === "typing" && board?.buzzed_by === playerName) {
      setTyped(""); setSubmitted(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [board?.phase, board?.buzzed_by, playerName]);

  // Показываем result
  useEffect(() => {
    if (!result) { setShowRes(null); return; }
    setShowRes(result);
    const t = setTimeout(() => setShowRes(null), 3000);
    return () => clearTimeout(t);
  }, [result]);

  // Убираем result при новом board
  useEffect(() => {
    if (board?.phase === "question" || board?.phase === "pick") setShowRes(null);
  }, [board?.phase, board?.current?.question]);

  if (!board) return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ffe600", fontSize:"1.2rem" }}>🏆 Загружаем...</div>
    </div>
  );

  const { categories, opened, scores, phase, current, buzzed_by, cant_buzz, wrong_answers, turn_player } = board;
  const openedSet  = new Set(opened || []);
  const myScore    = scores?.[playerName] ?? 0;
  const isMyTurn   = turn_player === playerName;
  const iAmBuzzed  = buzzed_by === playerName;
  const iAmBlocked = (cant_buzz || []).includes(playerName);
  const canBuzz    = phase === "question" && !buzzed && !iAmBlocked;
  const isTyping   = phase === "typing";

  const submit = () => {
    if (!typed.trim() || submitted) return;
    setSubmitted(true);
    onAnswer(typed.trim());
  };

  // ── Оверлей результата ─────────────────────────────────────────────────────
  const Overlay = () => {
    if (!showRes) return null;
    if (showRes.timeout) {
      return (
        <div style={{
          position:"fixed", inset:0, background:"#000000bb",
          display:"flex", alignItems:"center", justifyContent:"center",
          zIndex:50, padding:"1rem",
        }}>
          <div style={{
            background:"#1a1a2b", border:"3px solid #ffe600", borderRadius:"1.5rem",
            boxShadow:"6px 6px 0 #000", padding:"1.5rem 2rem", textAlign:"center", maxWidth:320,
          }}>
            <div style={{fontSize:"2.5rem", marginBottom:8}}>{showRes.revealed ? "👁" : "⏱"}</div>
            <div style={{fontFamily:"'Fredoka One',sans-serif", color:"#ffe600", fontSize:"1.25rem"}}>
              {showRes.revealed ? "Ответ раскрыт" : "Время вышло"}
            </div>
            {showRes.answer && (
              <div style={{fontFamily:"'Nunito',sans-serif", color:"#aaa", marginTop:8}}>
                Правильный ответ: <strong style={{color:"#fff"}}>{showRes.answer}</strong>
              </div>
            )}
          </div>
        </div>
      );
    }
    const isMe = showRes.name === playerName;
    const win  = showRes.correct && isMe;
    const lose = !showRes.correct && isMe;
    return (
      <div style={{
        position:"fixed", inset:0, background:"#000000bb",
        display:"flex", alignItems:"center", justifyContent:"center",
        zIndex:50, padding:"1rem",
      }}>
        <div style={{
          background: win ? "#0d2b1a" : lose ? "#2b0d0d" : "#1a1a2b",
          border: `3px solid ${win ? "#3dffa0" : lose ? "#ff3355" : "#9b5de5"}`,
          borderRadius:"1.5rem", boxShadow:"6px 6px 0 #000",
          padding:"1.5rem 2rem", textAlign:"center", maxWidth:300,
        }}>
          <div style={{ fontSize:"2.5rem", marginBottom:8 }}>{showRes.correct ? "✅" : "❌"}</div>
          {showRes.correct ? (
            <>
              <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#3dffa0", fontSize:"1.3rem" }}>
                {isMe ? `Верно! +${showRes.value}` : `${showRes.name} ответил верно!`}
              </div>
              {isMe && showRes.player_answer && (
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.82rem", marginTop:6 }}>
                  Твой ответ: «{showRes.player_answer}»
                </div>
              )}
              {showRes.answer && (
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.82rem", marginTop:4 }}>
                  Правильный ответ: <strong style={{color:"#fff"}}>{showRes.answer}</strong>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ff3355", fontSize:"1.2rem" }}>
                {isMe ? `Неверно! −${showRes.value}` : `${showRes.name} — неверно`}
              </div>
              {isMe && showRes.player_answer && (
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.82rem", marginTop:4 }}>
                  Твой ответ: «{showRes.player_answer}»
                </div>
              )}
              {showRes.answer && (
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.82rem", marginTop:4 }}>
                  Правильный ответ: <strong style={{color:"#fff"}}>{showRes.answer}</strong>
                </div>
              )}
              {showRes.remaining > 0 && (
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#666", fontSize:"0.75rem", marginTop:6 }}>
                  Ещё могут ответить: {showRes.remaining}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Вопрос / ввод ──────────────────────────────────────────────────────────
  if (phase === "question" || phase === "typing") {
    return (
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:"1rem" }}>
        <Overlay/>

        {/* Вопрос */}
        <div style={{
          background:"#1e0845", border:"3px solid #ffffff22", borderRadius:"1.25rem",
          boxShadow:"4px 4px 0 #000", padding:"0.9rem 1.1rem",
          textAlign:"center", width:"100%", maxWidth:400,
        }}>
          <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ffe600", fontSize:"0.8rem", marginBottom:6 }}>
            {categories?.[current?.cat_idx]?.category} — {current?.value} очков
          </div>
          <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#fff", fontSize:"1.25rem", lineHeight:1.3 }}>
            {current?.question}
          </div>
        </div>

        {/* Я ввожу ответ */}
        {isTyping && iAmBuzzed && (
          <div style={{ width:"100%", maxWidth:380, display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ff3cac", fontSize:"1.05rem", textAlign:"center" }}>
              ✍️ Введи ответ!
            </div>
            <input
              ref={inputRef}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              disabled={submitted}
              placeholder="Твой ответ..."
              style={{
                width:"100%", background:"#1e0845",
                border:"3px solid #ff3cac", borderRadius:"0.75rem",
                padding:"0.75rem 1rem", color:"#fff",
                fontFamily:"'Fredoka One',sans-serif", fontSize:"1.1rem",
                outline:"none", boxSizing:"border-box",
                opacity: submitted ? 0.5 : 1,
              }}
            />
            <button onClick={submit} disabled={!typed.trim() || submitted} style={{
              background: submitted ? "#333" : "#ff3cac",
              border:"3px solid #000", borderRadius:"1rem",
              boxShadow: submitted ? "none" : "4px 4px 0 #000",
              padding:"0.75rem", fontFamily:"'Fredoka One',sans-serif",
              fontSize:"1.1rem", color:"#fff",
              cursor: submitted ? "not-allowed" : "pointer",
            }}>
              {submitted ? "⏳ Проверяем..." : "✅ Ответить"}
            </button>
          </div>
        )}

        {/* Кто-то другой вводит */}
        {isTyping && !iAmBuzzed && (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#aaa", fontSize:"1rem" }}>
              ✍️ {buzzed_by} вводит ответ...
            </div>
            {/* Неверные ответы видны зрителям */}
            {Object.keys(wrong_answers || {}).length > 0 && (
              <div style={{ marginTop:12, background:"#2b0d0d", border:"1.5px solid #ff335533",
                borderRadius:"0.75rem", padding:"0.5rem 1rem", display:"inline-block" }}>
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ff3355", fontSize:"0.75rem", marginBottom:4 }}>Ответили неверно:</div>
                {Object.entries(wrong_answers).map(([n, a]) => (
                  <div key={n} style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.78rem" }}>
                    <strong style={{color:"#fff"}}>{n}</strong>: «{a}»
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Кнопка buzz */}
        {phase === "question" && (
          <button
            onTouchStart={e => { e.preventDefault(); if (!canBuzz) return; setPressing(true); }}
            onTouchEnd={e => { e.preventDefault(); setPressing(false); if (!canBuzz) return; setBuzzed(true); onBuzz(); }}
            onClick={() => { if (!canBuzz) return; setBuzzed(true); onBuzz(); }}
            disabled={!canBuzz}
            style={{
              width:"72vw", maxWidth:260, height:"72vw", maxHeight:260,
              borderRadius:"50%",
              background: iAmBlocked ? "#1a1a1a" : buzzed ? "#333"
                : pressing ? "radial-gradient(circle,#ff6ccc,#cc0080)"
                : "radial-gradient(circle,#ff3cac,#990066)",
              border: `5px solid ${iAmBlocked ? "#333" : buzzed ? "#555" : "#000"}`,
              boxShadow: pressing ? "2px 2px 0 #000" : (iAmBlocked||buzzed) ? "none" : "6px 6px 0 #000",
              transform: pressing ? "translate(3px,3px)" : "none",
              transition:"transform 0.08s, box-shadow 0.08s",
              cursor: canBuzz ? "pointer" : "not-allowed",
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              WebkitTapHighlightColor:"transparent", touchAction:"manipulation",
            }}
          >
            <span style={{ fontSize:"3rem", lineHeight:1 }}>🔔</span>
            <span style={{ fontFamily:"'Fredoka One',sans-serif", fontSize:"1.5rem",
              color:"#fff", textShadow:"2px 2px 0 #000", marginTop:8, textAlign:"center" }}>
              {iAmBlocked ? "❌ Блок" : buzzed ? "Нажато!" : "ЖАТЬ!"}
            </span>
          </button>
        )}

        {/* Неверные ответы в фазе вопроса */}
        {phase === "question" && Object.keys(wrong_answers || {}).length > 0 && (
          <div style={{ background:"#2b0d0d", border:"1.5px solid #ff335533",
            borderRadius:"0.75rem", padding:"0.4rem 0.85rem", textAlign:"center" }}>
            <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ff3355", fontSize:"0.72rem", marginBottom:3 }}>Ответили неверно:</div>
            {Object.entries(wrong_answers).map(([n, a]) => (
              <div key={n} style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:"0.75rem" }}>
                <strong style={{color:"#fff"}}>{n}</strong>: «{a}»
              </div>
            ))}
          </div>
        )}

        <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ffe600", fontSize:"1rem" }}>
          Мои очки: {myScore}
        </div>
      </div>
    );
  }

  // ── Выбор вопроса ──────────────────────────────────────────────────────────
  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"0.75rem", gap:8 }}>
      <Overlay/>
      <div style={{ textAlign:"center", marginBottom:4 }}>
        {isMyTurn
          ? <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#3dffa0", fontSize:"1rem",
              background:"#3dffa015", border:"2px solid #3dffa044", borderRadius:"0.75rem",
              padding:"0.4rem 0.75rem", display:"inline-block" }}>✨ Твоя очередь выбирать!</div>
          : <div style={{ fontFamily:"'Nunito',sans-serif", color:"#666", fontSize:"0.85rem" }}>
              Выбирает: <strong style={{color:"#aaa"}}>{turn_player}</strong>
            </div>
        }
      </div>
      <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ffe600", fontSize:"0.95rem", textAlign:"center" }}>
        Мои очки: {myScore}
      </div>
      <div style={{ overflowX:"auto", opacity: isMyTurn ? 1 : 0.5, pointerEvents: isMyTurn ? "auto" : "none" }}>
        <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:4 }}>
          <thead>
            <tr>{(categories||[]).map((cat, i) => (
              <th key={i} style={{
                background: CAT_COLORS[i%CAT_COLORS.length]+"22",
                border: `2px solid ${CAT_COLORS[i%CAT_COLORS.length]}55`,
                borderRadius:"0.5rem", padding:"0.3rem 0.2rem",
                fontFamily:"'Fredoka One',sans-serif", fontSize:"0.62rem",
                color: CAT_COLORS[i%CAT_COLORS.length], textAlign:"center",
              }}>{cat.category.replace(/^[^\s]+\s/,"")}</th>
            ))}</tr>
          </thead>
          <tbody>
            {VALUES.map(val => (
              <tr key={val}>{(categories||[]).map((cat, ci) => {
                const isOpen = openedSet.has(`${ci}:${val}`);
                return (
                  <td key={ci}
                    onClick={e => { e.preventDefault(); if (!isOpen && isMyTurn) onPick(ci, val); }}
                    onTouchStart={e => { if (!isOpen && isMyTurn) e.currentTarget.style.transform="scale(0.93)"; }}
                    onTouchEnd={e => { e.currentTarget.style.transform="scale(1)"; }}
                    style={{
                      background: isOpen ? "#0a0a14" : "#1e0845",
                      border: isOpen ? "2px solid #ffffff0a" : `2px solid ${CAT_COLORS[ci%CAT_COLORS.length]}44`,
                      borderRadius:"0.5rem", textAlign:"center",
                      padding:"0.65rem 0.2rem",
                      cursor: isOpen||!isMyTurn ? "default" : "pointer",
                      WebkitTapHighlightColor:"transparent", touchAction:"manipulation",
                    }}
                  >
                    <span style={{
                      fontFamily:"'Fredoka One',sans-serif", fontSize:"0.95rem",
                      color: isOpen ? "#2a2a3a" : "#ffe600",
                      textShadow: isOpen ? "none" : "1px 1px 0 #000",
                    }}>{isOpen ? "✓" : val}</span>
                  </td>
                );
              })}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}